import {
  DateI18nProvider,
  DatePicker,
  getLocalTimeZone,
  parseDate,
  Text,
  Tooltip,
} from '@box/blueprint-web';
import classNames from 'classnames';
import * as React from 'react';
import type { IntlShape } from 'react-intl';
import { injectIntl } from 'react-intl';
import { BuilderInlineError } from '../../../components/new-file-request-builder/new-form-element/builder-inline-error';
import { dateFromZuluTime, dateToZuluTime } from '../../utils/time';
import useId from '../../utils/useId';
import css from './blueprint-date-field.module.scss';
import messages from './messages';

type Props = {
  className?: string;
  description?: React.ReactNode;
  error?: React.ReactNode | string;
  infoText?: React.ReactNode | string;
  intl: IntlShape;
  isDisabled?: boolean;
  isRequired?: boolean;
  label: React.ReactNode;
  onChange: (...args: unknown[]) => unknown;
  value: string;
  isSubmitter?: boolean;
};

const getDatePickerValue = (date?: Date | null) => {
  if (!date) {
    return null;
  }

  // Prevent parseDate from throwing error before the date is completely typed
  const datePickerFormatString = [
    `${date.getFullYear()}`.padStart(4, '0'),
    `${date.getMonth() + 1}`.padStart(2, '0'),
    `${date.getDate()}`.padStart(2, '0'),
  ].join('-');

  return parseDate(datePickerFormatString);
};

const BlueprintDateField = React.memo<Props>((props: Props) => {
  const {
    className = '',
    label,
    description,
    error,
    infoText,
    isRequired,
    isDisabled,
    onChange,
    value,
    intl: { formatMessage, locale },
    isSubmitter,
  } = props;

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  const handleChangeDate = (date: any) => {
    /*
     * DateValue from react-spectrum represents the date without a timezone.
     * https://react-spectrum.adobe.com/internationalized/date/CalendarDate.html#to-a-string
     *
     * To precisely handle partially-typed date string, for example, 09-08-2 (MM-DD-YYYY)
     * we apply dateToZuluTime (based on date-fns) to ensure the date is precisely created and converted to UTC time.
     * */
    const zuluAdjustedTime = date
      ? dateToZuluTime(date.toDate(getLocalTimeZone())).toISOString()
      : undefined;

    onChange(zuluAdjustedTime);
  };

  const classes = classNames(className, css.dateFieldContainer);

  /*
   * We need dateFromZuluTime because the stored date is in UTC (Zulu) time,
   * and we need to convert it to the local timezone.
   */
  const dateObject = value ? dateFromZuluTime(new Date(value)) : null;
  const dateValue = getDatePickerValue(dateObject);

  const descriptionID = useId('description');
  const labelID = useId('label');
  const ariaAttrs = {
    'aria-describedby': description ? descriptionID : undefined,
    'aria-labelledby': labelID,
  } as const;

  return (
    <div className={classes} data-testid="blueprint-date-field">
      <Text
        id={labelID}
        as="label"
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2820
        color="text-on-light-default"
        variant="subtitle"
        className={classNames({ [css.label]: isSubmitter })}
      >
        {label}
      </Text>
      <DateI18nProvider locale={locale}>
        <Tooltip
          className="field-tooltip"
          content={infoText}
          open={infoText ? undefined : false}
          side="right"
        >
          <div>
            {/*
             // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322 */}
            <DatePicker
              calendarAriaLabel={formatMessage(messages.calendarAriaLabel)}
              className={css.datePicker}
              clearDatePickerAriaLabel={formatMessage(
                messages.clearDatePickerAriaLabel,
              )}
              error={error}
              isDisabled={isDisabled}
              isRequired={isRequired}
              nextMonthAriaLabel={formatMessage(messages.nextMonthAriaLabel)}
              onChange={handleChangeDate}
              previousMonthAriaLabel={formatMessage(
                messages.previousMonthAriaLabel,
              )}
              value={dateValue}
              {...ariaAttrs}
            />
          </div>
        </Tooltip>
      </DateI18nProvider>

      {!!description && (
        <Text
          as="span"
          className={css.description}
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
          color="textOnLight"
          id={descriptionID}
          variant="bodyDefault"
        >
          {description}
        </Text>
      )}

      <BuilderInlineError />
    </div>
  );
});

type WrappedProps = Omit<Props, 'intl'>;
const Wrapped = injectIntl(BlueprintDateField);
export default Wrapped as React.ComponentType<WrappedProps>;
